/**
 * Client-Side Encryption Utility
 * Uses Web Crypto API for AES-GCM encryption
 * Implements key derivation from user credentials
 */

// Check if Web Crypto API is available
function isCryptoAvailable(): boolean {
  return typeof crypto !== 'undefined' && 
         typeof crypto.subtle !== 'undefined' &&
         typeof crypto.getRandomValues === 'function';
}

// Throw friendly error if crypto is not available
function ensureCryptoAvailable(): void {
  if (!isCryptoAvailable()) {
    throw new Error('Web Crypto API is not available. Please use HTTPS or update your browser.');
  }
}

const ENCRYPTION_ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const SALT_LENGTH = 16;
const PBKDF2_ITERATIONS = 100000;

interface EncryptedData {
  ciphertext: string;
  iv: string;
  salt: string;
  version: string;
}

/**
 * Generate a cryptographic key from a passphrase using PBKDF2
 */
async function deriveKey(
  passphrase: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passphraseKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    passphraseKey,
    { name: ENCRYPTION_ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Generate a device-specific encryption key
 * Combines multiple device fingerprints for uniqueness
 */
export function generateDeviceFingerprint(): string {
  try {
    const components = [
      navigator.userAgent,
      navigator.language,
      new Date().getTimezoneOffset().toString(),
      screen.colorDepth.toString(),
      screen.width.toString() + 'x' + screen.height.toString(),
      // Add a random component stored in localStorage for persistence
      getOrCreateDeviceId(),
    ];

    return components.join('|');
  } catch (error) {
    console.error('Failed to generate device fingerprint:', error);
    // Fallback to a simpler fingerprint
    return `${navigator.userAgent}|${Date.now()}|${Math.random()}`;
  }
}

/**
 * Get or create a device ID with error handling
 */
function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem('device_id');
    if (existing) {
      return existing;
    }
    
    const newId = generateRandomId();
    localStorage.setItem('device_id', newId);
    return newId;
  } catch (error) {
    // If localStorage fails, use a session-based ID
    console.warn('localStorage not available, using session-based ID');
    if (!(window as any).__tension_device_id) {
      (window as any).__tension_device_id = generateRandomId();
    }
    return (window as any).__tension_device_id;
  }
}

/**
 * Generate a random ID with fallback for browsers without crypto.randomUUID()
 */
function generateRandomId(): string {
  // Try modern crypto.randomUUID() first (not available in all mobile browsers)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch (e) {
      console.debug('crypto.randomUUID() failed, using fallback');
    }
  }
  
  // Fallback: generate a UUID v4-like string manually
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Create a user-specific encryption passphrase
 * Combines device fingerprint with optional user PIN/password
 */
export function createEncryptionPassphrase(userSecret?: string): string {
  const deviceFingerprint = generateDeviceFingerprint();
  const appSecret = 'TENSION_v1_2026'; // App-specific constant
  
  if (userSecret) {
    // If user has set a PIN/password, include it
    return `${appSecret}:${deviceFingerprint}:${userSecret}`;
  }
  
  // Otherwise use device fingerprint only
  return `${appSecret}:${deviceFingerprint}`;
}

/**
 * Encrypt data using AES-GCM
 */
export async function encryptData(
  data: any,
  passphrase: string
): Promise<string> {
  ensureCryptoAvailable();
  
  try {
    // Convert data to JSON string
    const jsonString = JSON.stringify(data);
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(jsonString);

    // Generate random salt and IV
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

    // Derive encryption key from passphrase
    const key = await deriveKey(passphrase, salt);

    // Encrypt the data
    const ciphertext = await crypto.subtle.encrypt(
      {
        name: ENCRYPTION_ALGORITHM,
        iv: iv,
      },
      key,
      dataBuffer
    );

    // Package encrypted data with metadata
    const encrypted: EncryptedData = {
      ciphertext: arrayBufferToBase64(ciphertext),
      iv: uint8ArrayToBase64(iv),
      salt: uint8ArrayToBase64(salt),
      version: '1.0',
    };

    return JSON.stringify(encrypted);
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Decrypt data using AES-GCM
 */
export async function decryptData(
  encryptedString: string,
  passphrase: string
): Promise<any> {
  ensureCryptoAvailable();
  
  try {
    // Parse encrypted data package
    const encrypted: EncryptedData = JSON.parse(encryptedString);

    // Convert base64 strings back to buffers
    const ciphertext = base64ToArrayBuffer(encrypted.ciphertext);
    const iv = base64ToArrayBuffer(encrypted.iv);
    const salt = base64ToArrayBuffer(encrypted.salt);

    // Derive decryption key from passphrase
    const key = await deriveKey(passphrase, new Uint8Array(salt));

    // Decrypt the data
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: ENCRYPTION_ALGORITHM,
        iv: new Uint8Array(iv),
      },
      key,
      ciphertext
    );

    // Convert decrypted buffer to string and parse JSON
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decryptedBuffer);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Decryption failed:', error);
    throw new Error('Failed to decrypt data - data may be corrupted or passphrase incorrect');
  }
}

/**
 * Calculate SHA-256 hash of data for integrity checking
 */
export async function calculateHash(data: any): Promise<string> {
  const encoder = new TextEncoder();
  const dataString = JSON.stringify(data);
  const dataBuffer = encoder.encode(dataString);
  
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  return arrayBufferToBase64(hashBuffer);
}

/**
 * Verify data integrity using hash
 */
export async function verifyIntegrity(
  data: any,
  expectedHash: string
): Promise<boolean> {
  const actualHash = await calculateHash(data);
  return actualHash === expectedHash;
}

// Helper functions for base64 encoding/decoding
function uint8ArrayToBase64(bytes: Uint8Array): string {
  const sliced = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  return arrayBufferToBase64(sliced as ArrayBuffer);
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Securely wipe data from memory (best effort)
 * Note: JavaScript doesn't provide true memory wiping, but this helps
 */
export function secureWipe(obj: any): void {
  if (typeof obj === 'object' && obj !== null) {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (typeof obj[key] === 'string') {
          obj[key] = '';
        } else if (typeof obj[key] === 'number') {
          obj[key] = 0;
        } else if (typeof obj[key] === 'object') {
          secureWipe(obj[key]);
        }
        delete obj[key];
      }
    }
  }
}